import { Button, Skeleton } from '@up2stay/up2promo-ui';
import { BarChart3, Download } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import {
  type ComparisonMode,
  DateRangePicker,
  type DateRangeValue,
  isComparisonSupported,
  rangeValueFromPreset,
} from '@/core/components/date-range-picker';
import { ListEmptyState } from '@/core/components/layout';
import { ReputationPushCta } from '@/core/components/notifications/reputation-push-cta';
import { PageHeader } from '@/core/components/page-header';
import {
  analyticsCsvFilename,
  buildAnalyticsCsv,
} from '@/core/components/reputation/analytics/analytics-export';
import { comparisonWindow } from '@/core/components/reputation/analytics/analytics-format';
import { AnalyticsLocationsCard } from '@/core/components/reputation/analytics/analytics-locations-card';
import { AnalyticsMentionsCard } from '@/core/components/reputation/analytics/analytics-mentions-card';
import { AnalyticsRatingCard } from '@/core/components/reputation/analytics/analytics-rating-card';
import {
  type AnalyticsMetric,
  AnalyticsTrendPanel,
} from '@/core/components/reputation/analytics/analytics-trend-panel';
import { LocationCombobox } from '@/core/components/reputation/location-combobox';
import {
  ReputationLayout,
  ReputationPageBanners,
} from '@/core/components/reputation/reputation-layout';
import { ReputationPageBreadcrumb } from '@/core/components/reputation/reputation-page-breadcrumb';
import {
  useReputationScopeLocation,
  useReputationSync,
} from '@/core/components/reputation/reputation-sync-context';
import { ReputationSyncPlaceholder } from '@/core/components/reputation/reputation-sync-placeholder';
import { useLocale } from '@/core/hooks/use-locale';
import { useLocations } from '@/core/hooks/use-locations';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useReputationAnalytics } from '@/core/hooks/use-reputation';
import { downloadCsvFile } from '@/core/lib/download-csv';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

const LOCATIONS_LIMIT = 200;
const ALL = '__all__';

export function ReputationAnalyticsPage() {
  return (
    <AppIonPage>
      <AppPageContent>
        <ReputationLayout>
          <ReputationAnalyticsPageContent />
        </ReputationLayout>
      </AppPageContent>
    </AppIonPage>
  );
}

function ReputationAnalyticsPageContent() {
  const dict = useDictionary();
  const a = dict.reputation.analytics;
  const scopeDict = dict.reputation.scope;
  const { navigateShell } = useNavigateShell();
  const routerLocation = useLocation();
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? '';
  const locale = useLocale();

  const scope = useMemo(() => {
    const id = new URLSearchParams(routerLocation.search).get('locationId');
    return id && id.length > 0 ? id : ALL;
  }, [routerLocation.search]);

  const handleScopeChange = (next: string) => {
    const params = new URLSearchParams(routerLocation.search);
    if (next === ALL) params.delete('locationId');
    else params.set('locationId', next);
    const search = params.toString();
    navigateShell(`${routerLocation.pathname}${search ? `?${search}` : ''}`, {
      intent: 'updateQuery',
    });
  };

  const [dateRange, setDateRange] = useState<DateRangeValue>(() =>
    rangeValueFromPreset('d30'),
  );
  const [comparison, setComparison] = useState<ComparisonMode>('prevPeriod');
  const [metric, setMetric] = useState<AnalyticsMetric>('reviews');
  const locationId = scope === ALL ? undefined : scope;
  useReputationScopeLocation(locationId);
  const { isSyncCompleteForScope } = useReputationSync();

  const handleDateRangeChange = (next: DateRangeValue) => {
    setDateRange(next);
    if (!isComparisonSupported(next)) setComparison('none');
  };

  const { locations } = useLocations(workspaceId || undefined, {
    limit: LOCATIONS_LIMIT,
  });
  const locationNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const loc of locations) map.set(loc.id, loc.name ?? loc.id);
    return map;
  }, [locations]);

  const { data, isLoading } = useReputationAnalytics(workspaceId || undefined, {
    locationId,
    from: dateRange.from,
    to: dateRange.to,
  });

  // Comparison window only exists when the range has a defined start.
  const cmpWindow = isComparisonSupported(dateRange)
    ? comparisonWindow(dateRange.from, dateRange.to, comparison)
    : null;
  const { data: cmpData } = useReputationAnalytics(
    cmpWindow ? workspaceId || undefined : undefined,
    { locationId, from: cmpWindow?.from, to: cmpWindow?.to },
  );
  const prev = cmpWindow && cmpData ? cmpData : undefined;
  const vsLabel =
    comparison === 'prevYear' ? a.comparison.vsYear : a.comparison.vsPeriod;

  const isEmpty = !isLoading && data != null && data.totalReviews === 0;
  const benchmarkRows = data?.locations ?? [];

  const drillDown = (aspectKey: string) => {
    const params = new URLSearchParams({ aspect: aspectKey });
    if (locationId) params.set('locationId', locationId);
    navigateShell(`${shellPaths.reputationInbox}?${params.toString()}`);
  };
  const drillDownCell = (locId: string, aspectKey: string) => {
    const params = new URLSearchParams({
      locationId: locId,
      aspect: aspectKey,
    });
    navigateShell(`${shellPaths.reputationInbox}?${params.toString()}`);
  };

  const exportCsv = () => {
    if (!data) return;
    const csv = buildAnalyticsCsv(data, locationNames, a.export.columns);
    downloadCsvFile(csv, analyticsCsvFilename())
      .then(() => toast.success(a.export.success))
      .catch(() => toast.error(a.export.error));
  };

  const renderLocationPicker = (className: string) => (
    <LocationCombobox
      value={scope}
      onChange={handleScopeChange}
      options={locations.map((loc) => ({
        id: loc.id,
        name: loc.name ?? loc.id,
      }))}
      allValue={ALL}
      allLabel={scopeDict.allLocations}
      searchPlaceholder={scopeDict.searchPlaceholder}
      emptyText={scopeDict.noResults}
      className={className}
    />
  );

  return (
    <AppPageLayout>
      <PageHeader
        title={a.title}
        eyebrow={[
          scope === ALL
            ? scopeDict.allLocations
            : (locationNames.get(scope) ?? scope),
          comparison !== 'none' ? vsLabel : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            <div className="hidden md:block">
              {renderLocationPicker('w-auto max-w-xs')}
            </div>
            <DateRangePicker
              value={dateRange}
              onChange={handleDateRangeChange}
              align="end"
              comparison={comparison}
              onComparisonChange={setComparison}
            />
            <Button
              variant="outline"
              className="hidden md:inline-flex"
              disabled={!data || isEmpty}
              onClick={exportCsv}
            >
              <Download className="mr-2 size-4" aria-hidden />
              {a.export.label}
            </Button>
          </>
        }
        breadcrumb={
          <ReputationPageBreadcrumb pageLabel={dict.nav.reputationAnalytics} />
        }
      />
      <ReputationPageBanners />
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <ReputationPushCta dict={a.pushCta} />

        <div className="flex flex-col gap-2.5 md:hidden">
          {renderLocationPicker('w-full')}
          <Button
            variant="outline"
            disabled={!data || isEmpty}
            onClick={exportCsv}
          >
            <Download className="mr-2 size-4" aria-hidden />
            {a.export.label}
          </Button>
        </div>

        {!isSyncCompleteForScope && !isLoading && (
          <ReputationSyncPlaceholder kind="analytics" />
        )}

        {isSyncCompleteForScope && isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-28 w-full rounded-[var(--r-card)]" />
            <Skeleton className="h-64 w-full rounded-[var(--r-card)]" />
          </div>
        )}

        {isSyncCompleteForScope && isEmpty && (
          <ListEmptyState
            icon={<BarChart3 className="h-5 w-5" />}
            title={a.empty}
          />
        )}

        {isSyncCompleteForScope && !isLoading && data && !isEmpty && (
          <>
            <AnalyticsTrendPanel
              data={data}
              prev={prev}
              metric={metric}
              onMetricChange={setMetric}
              labels={a}
              locale={locale}
              vsLabel={vsLabel}
            />

            <div className="grid min-w-0 gap-3 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
              <AnalyticsMentionsCard
                aspects={data.aspects}
                topics={data.topics}
                labels={a}
                onSelectAspect={drillDown}
              />
              <AnalyticsRatingCard
                className="max-md:hidden"
                distribution={data.ratingDistribution}
                total={data.totalReviews}
                title={a.ratingDistribution.title}
                subtitle={a.ratingDistribution.subtitle}
              />
            </div>

            {benchmarkRows.length > 0 ? (
              <AnalyticsLocationsCard
                rows={benchmarkRows}
                names={locationNames}
                heatmap={data.heatmap}
                labels={a}
                locale={locale}
                onSelectLocation={handleScopeChange}
                onDrilldown={drillDownCell}
              />
            ) : null}
          </>
        )}
      </div>
    </AppPageLayout>
  );
}
